import React, { Fragment, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { engineeringEntryActions, projectEntryActions, bulkEntryActions } from '@/store';
import { TotalHoursFooter } from '../ProjectEntry/components';
import EngineeringProjectRow from './EngineeringProjectRow.jsx';
import AddEngineeringProject from './components/AddEngineeringProject.jsx';
import { ProjectEntriesWrapper } from '../ProjectEntry/styled.js';
import { projectEntriesError } from '../../shared/index.js';
import { isArrayWithLength } from '../../helpers/utils.js';
import PageLoader from '../../components/PageLoader/PageLoader.jsx';
import { handleFetchError, handleFetchSuccess } from '../../helpers/toastrUtils.jsx';
import SelectedDaysHeader from '../Home/components/SelectedDaysHeader.jsx';
import CopyPreviousButton from '../../components/CopyPreviousButton/CopyPreviousButton.jsx';
import {
  ButtonContainer,
} from './components/styled.js';
import TimeBucket from '../TimeBucket/TimeBucket.jsx';

const EngineeringTimesheet = () => {
  const dispatch = useDispatch();

  const oid = useSelector(state => state.user.activeUser?.oid);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const engineeringEntries = useSelector(state => state.engineeringEntry.engineeringEntries);
  const totalHoursByDate = useSelector(state => state.timeBucket.totalHoursByDate);
  const affectedWeek = useSelector(state => state.projectEntry.projectEntries.selectedDays[0]);

  const [loading, setLoading] = useState(true);
  const [copyPreviousLoading, setCopyPreviousLoading] = useState(false);
  const [copyPreviousWarningOpen, setCopyPreviousWarningOpen] = useState(false);

  const startDate = moment(selectedDays[0]).format('YYYY-MM-DD');
  const endDate = moment(selectedDays[6]).format('YYYY-MM-DD');

  useEffect(() => {
    const shouldRun = oid && isArrayWithLength(selectedDays);
    if (!shouldRun) return;

    const runAsyncTasks = async () => {
      try {
        await dispatch(projectEntryActions.setupCategories(oid));
        await dispatch(projectEntryActions.requestMonthlyCompletion(oid, moment(selectedDays[0]).format(), 0));
        await dispatch(engineeringEntryActions.getEngineeringProjectTimeEntries(oid, startDate, endDate));
      } catch (e) {
        handleFetchError(e, projectEntriesError);
      }
    };

    setLoading(true);
    runAsyncTasks().finally(() => setLoading(false));
  }, [oid, selectedDays]);

  const totalHours = useMemo(() => {    
    const weekdates = selectedDays.map(date => moment(date).format('YYYY-MM-DD'));
    const projectSums = Array(7).fill(0);

    engineeringEntries.data.forEach(project => {
      project.entries.forEach(entry => {
        const entryDate = entry.entryDate;
        const dateIndex = weekdates.indexOf(entryDate);

        if (weekdates.indexOf(entryDate) !== -1) {
          projectSums[dateIndex] += entry.hours || 0;
        }
      });
    });

    totalHoursByDate && Object.keys(totalHoursByDate).forEach(key => {
      const categorySums = totalHoursByDate[key];
      Object.keys(categorySums).forEach(date => {
        const dateIndex = weekdates.indexOf(date);
        if (dateIndex !== -1) {
          projectSums[dateIndex] += categorySums[date] || 0;
        }
      });
    });

    return projectSums;
  }, [engineeringEntries.data, selectedDays, totalHoursByDate]);

  const handleAddProject = (project, editableDates) => {
    const addedProject = {
      projectDescription: project.projectName,
      projectNumber: project.projectNumber,
      projectId: project.projectId,
      canEditByDay: { ...editableDates },
      entries: []
    };

    const payload = {
      oid,
      affectedWeek,
      action: 'ADD',
      selectedProject: { ...addedProject, type: 'VPIN' },
    };

    // Updates Systems container to populate dropdown
    dispatch(projectEntryActions.addTask(payload, 'VPIN'));
    dispatch(engineeringEntryActions.deleteUserTimesheetProjectBlacklist(addedProject, oid))
  }
  const handleDeleteProject = (projectId) => {
    dispatch(engineeringEntryActions.addEngineeringEntryBlacklist(projectId, oid));
  }

  const handleCopyWeek = async (e) => {
    e.stopPropagation();
    setCopyPreviousLoading(true);
    try {
      const resp = await dispatch(bulkEntryActions.copyPreviousWeek(oid, startDate, endDate, 'VPIN'));
      if (resp.item2 && resp.item2.length > 0) {
        let errorMessage= 'An error occurred while copying the previous week. \n';
        resp.item2.forEach(err => {        
          errorMessage += `${err.data.Id} - ${err.data.EntryDate}: ${err.message}\n`;
        });
        const errorInfo = {
          title: 'Error Copying Previous Week',
          message: errorMessage,
          timerOverride: 10000
        }
        handleFetchError(e, errorInfo);
      } else
        handleFetchSuccess('Previous week copied successfully');
    } finally {
      setCopyPreviousLoading(false);
      setCopyPreviousWarningOpen(false);
    }
  }

  const handleClickChange = (eventOrOpen) => {
    if (typeof eventOrOpen === 'boolean') {
      setCopyPreviousWarningOpen(eventOrOpen);
      return;
    }

    eventOrOpen.stopPropagation();
    setCopyPreviousWarningOpen(copyPreviousWarningOpen => !copyPreviousWarningOpen);
  }

  return (
    <Fragment>
      {!loading && !engineeringEntries.isLoading ? (
        <>
          <ButtonContainer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <CopyPreviousButton  copyPreviousLoading={copyPreviousLoading}
              copyPreviousWarningOpen={copyPreviousWarningOpen}
              onToggleWarning={handleClickChange}
              onCopyWeek={handleCopyWeek}
            />

            <div style={{ display: 'flex', gap: '1rem' }}>
              <AddEngineeringProject firstDayOfTheWeek={selectedDays[0]} onAddProject={handleAddProject} localEntries={engineeringEntries.data} />
            </div>
          </ButtonContainer>

          <SelectedDaysHeader />

          <ProjectEntriesWrapper>
            {engineeringEntries.data.map((project, index) => <EngineeringProjectRow project={project} key={index} handleDeleteProject={handleDeleteProject} />)}
          </ProjectEntriesWrapper>
          <TimeBucket />
          <TotalHoursFooter totalHours={totalHours} applyDeletableRowStyles="true" />
        </>
      ) : (
        <PageLoader />
      )}
    </Fragment>
  )
}

export default EngineeringTimesheet;